import {
  BellIcon,
  BookOpenIcon,
  ChevronDown,
  CrownIcon,
  LogOutIcon,
  Moon,
  RefreshCwOff,
  Sun,
  UserIcon,
} from "lucide-react";
import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useTheme } from "../contexts/ThemeContext";
import { Button } from "./ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";

const Header: React.FC = () => {
  const {
    user,
    logout,
    isPremium,
    premiumType,
    subscribeBasic,
    subscribePro,
    unsubscribe,
  } = useAuth();
  const { darkMode, toggleDarkMode } = useTheme();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  const handleSubscribeBasic = async () => {
    try {
      await subscribeBasic();
      // Opcional: mostrar un mensaje de éxito
    } catch (error) {
      // Opcional: mostrar un mensaje de error
      console.error("Error al suscribirse al plan Basic:", error);
    }
  };

  const handleSubscribePro = async () => {
    try {
      await subscribePro();
      // Opcional: mostrar un mensaje de éxito
    } catch (error) {
      // Opcional: mostrar un mensaje de error
      console.error("Error al suscribirse al plan Pro:", error);
    }
  };

  const handleUnsubscribe = async () => {
    try {
      await unsubscribe();
      // Opcional: mostrar un mensaje de éxito
    } catch (error) {
      // Opcional: mostrar un mensaje de error
      console.error("Error al cancelar suscripción:", error);
    }
  };

  // const toggleMenu = () => {
  //   setMenuOpen((prev) => !prev);
  // };

  const handleClickOutside = (event: MouseEvent) => {
    if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
      setMenuOpen(false);
    }
  };

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Función para obtener el texto del tipo de premium
  const getPremiumText = () => {
    switch (premiumType) {
      case "basic":
        return "Premium Basic";
      case "pro":
        return "Premium Pro";
      default:
        return "";
    }
  };

  return (
    <header className="py-4 px-6 bg-background header-shadow sticky top-0 z-50">
      <div className="container mx-auto flex justify-between items-center">
        <div className="flex items-center">
          <Link to="/dashboard" className="flex items-center group">
            <div className="p-2 rounded-lg bg-primary/10 mr-3 group-hover:bg-primary/20 transition-colors">
              <BookOpenIcon className="h-6 w-6 text-primary" />
            </div>
            <h1 className="text-xl font-bold gradient-text">BOE Fácil</h1>
          </Link>
        </div>

        <div className="flex items-center gap-4 relative">
          {user && !isPremium && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button className="premium-button">
                  <CrownIcon className="h-4 w-4 mr-1" />
                  Hazte Premium
                  <ChevronDown className="h-4 w-4 ml-1" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="w-56">
                <DropdownMenuLabel>Elige tu plan</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={handleSubscribeBasic}
                  className="cursor-pointer"
                >
                  <div className="flex flex-col py-1">
                    <span className="font-bold">Premium Basic</span>
                    <span className="text-xs text-muted-foreground">
                      Acceso a estadísticas básicas y exportación PDF
                    </span>
                  </div>
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={handleSubscribePro}
                  className="cursor-pointer"
                >
                  <div className="flex flex-col py-1">
                    <span className="font-bold">Premium Pro</span>
                    <span className="text-xs text-muted-foreground">
                      Todas las funciones premium y estadísticas avanzadas
                    </span>
                  </div>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}

          {isPremium && (
            <div className="premium-badge flex items-center">
              <CrownIcon className="h-3 w-3 mr-1" />
              <span className="text-xs font-medium">{getPremiumText()}</span>
            </div>
          )}

          <span className="text-sm font-medium">
            {user &&
              `Bienvenido, ${
                user.name || user.email?.split("@")[0] || "Usuario"
              }`}
          </span>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="rounded-lg h-9 w-9 bg-secondary/50 hover:bg-secondary"
              >
                <UserIcon className="h-5 w-5 text-primary" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel>Mi perfil</DropdownMenuLabel>
              <DropdownMenuSeparator />

              <DropdownMenuItem asChild className="cursor-pointer">
                <Link to="/alertas" className="flex items-center w-full">
                  <BellIcon className="h-4 w-4 mr-2 text-primary" />
                  Mis Alertas
                </Link>
              </DropdownMenuItem>

              {isPremium && (
                <>
                  {premiumType === "basic" && (
                    <DropdownMenuItem
                      onClick={handleSubscribePro}
                      className="cursor-pointer"
                    >
                      <CrownIcon className="h-4 w-4 mr-2 text-amber-500" />
                      Mejorar a Premium Pro
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuItem
                    onClick={handleUnsubscribe}
                    className="cursor-pointer"
                  >
                    <RefreshCwOff className="h-4 w-4 mr-2 text-amber-500" />
                    Cancelar Premium
                  </DropdownMenuItem>
                </>
              )}
              <DropdownMenuItem
                onClick={toggleDarkMode}
                className="cursor-pointer"
              >
                {darkMode ? (
                  <Sun className="h-4 w-4 mr-2 text-amber-400" />
                ) : (
                  <Moon className="h-4 w-4 mr-2 text-primary" />
                )}
                {darkMode ? "Tema: Claro" : "Tema: Oscuro"}
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={handleLogout}
                className="cursor-pointer text-destructive"
              >
                <LogOutIcon className="h-4 w-4 mr-2" />
                Cerrar sesión
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </header>
  );
};

export default Header;
